import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SessionService } from '@com-delta-idp/mnx-oauth';
import {
  CommonConstants,
  EndpointKeys,
  IDP_APPS,
  Libraries
} from '@com-delta-idp/mnx-skymiles-shared';
import {
  ApiBuilder,
  AppConfigService,
  CustomErrorBase,
  HttpHeadersPayload,
  HttpRequestMethods,
  handleError,
  logCustomError,
  logError
} from '@com-delta-idp/mnx-utils';
import { Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  EDocAPIErrorResponse,
  EDocRequest,
  EDocsResponse,
  VouchersResponse
} from '../../models/api/upgrades.interface';
import { TYPE, upgradesHeaderInfo } from '../../models/overview.constants';

@Injectable({
  providedIn: 'root'
})
export class DocsService {
  constructor(
    private http: HttpClient,
    private appConfig: AppConfigService,
    private session: SessionService
  ) {}

  getVouchersResponse = (): Observable<VouchersResponse> => {
    upgradesHeaderInfo['Authorization'] =
      TYPE.BEARER + ' ' + this.session.token.access_token;
    return this.getData<VouchersResponse>(
      upgradesHeaderInfo,
      EndpointKeys['SKYMILES_API_VOUCHERS'],
      {} as EDocRequest,
      false
    );
  };

  getEDocsResponse = (request: EDocRequest): Observable<EDocsResponse> => {
    return this.getData<EDocsResponse>(
      {},
      EndpointKeys['SKYMILES_API_ECREDITS'],
      request,
      true
    );
  };

  getData = <
    DocsType extends
      | EDocsResponse
      | VouchersResponse
      | CustomErrorBase
      | EDocAPIErrorResponse
  >(
    headerInfo: HttpHeadersPayload,
    key: string,
    payLoad: EDocRequest = {} as EDocRequest,
    withCredentials: boolean
  ): Observable<DocsType> =>
    new ApiBuilder<unknown, DocsType>(this.http, this.appConfig)
      .app(IDP_APPS.SKYMILES)
      .endpointKey(key)
      .method(HttpRequestMethods['POST'])
      .httpHeaders(headerInfo)
      .payload(payLoad)
      .withCredentials(withCredentials)
      .timeOut(CommonConstants.MAX_TIMEOUT)
      .errorHandler(handleError.bind(this))
      .library(Libraries.OVERVIEW)
      .create()
      .pipe(
        map((response: DocsType) => {
          return this.existCustomError<DocsType>(response, key);
        })
      );

  existCustomError = <
    DocsType extends
      | EDocsResponse
      | EDocAPIErrorResponse
      | VouchersResponse
      | CustomErrorBase
  >(
    response: DocsType,
    key: string
  ): DocsType => {
    if ('code' in response) {
      logCustomError(
        response as CustomErrorBase,
        IDP_APPS.SKYMILES,
        Libraries.OVERVIEW,
        key
      );
      throw throwError(response);
    } else if ('paymentError' in response) {
      if (
        response.paymentError.error.message.code !== TYPE.EDOCS_NO_DOCUMENT_CODE
      ) {
        this.logEDocsError(
          response,
          IDP_APPS.SKYMILES,
          Libraries.OVERVIEW,
          key
        );
        throw throwError(response);
      } else {
        return {} as DocsType;
      }
    }
    return response;
  };

  private logEDocsError = (
    customError: EDocAPIErrorResponse,
    app: string,
    library: string,
    feature: string
  ) => {
    const error: Error = {
      name: customError.paymentError.error.message.code,
      message: `${customError.paymentError.error.message.message}`.trim()
    };

    logError(error, app, library, feature);
  };
}
